import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import GameCard from 'components/games/tools/GameCard';
import { useMappedState } from 'redux-react-hook';
import { GUEST_ROUTES } from 'routes/routes';
import { useGetMyLikedGamesIdsQuery } from 'services/api/gamesApi';
import styled from 'styled-components';

interface Scenario {
  id: string;
  name: string;
  alias: string;
  publicName: string;
  publicDescription: string;
  publicRating: string;
  isFree: boolean;
  coverUrl: string;
  hasPhotos: boolean;
  likesNumber: number;
  tags: string[];
  videoCoverUrl?: string;
  isPublic: boolean;
  active: boolean;
  isLocked?: boolean;
}

interface ScenariosSectionProps {
  scenarios: Scenario[];
  title?: string;
  onOpenAuthModal: () => void;
  onOpenSubscriptionModal: () => void;
}

const ScenariosSection: React.FC<ScenariosSectionProps> = ({
  scenarios,
  title = "Choose your chat",
  onOpenAuthModal,
  onOpenSubscriptionModal
}) => {
  const navigate = useNavigate();
  const { data: myLikedGamesIds = [] } = useGetMyLikedGamesIdsQuery();

  const { hasSubscription, isSignedIn } = useMappedState(
    useCallback(state => {
      const currentUser = state.users.usersMap.get(state.users.currentUserId);
      const hasSubscription = currentUser?.hasSubscription;

      return {
        hasSubscription: hasSubscription,
        isSignedIn: state.users.currentUserId
      };
    }, [])
  );

  const handleGameCardClick = (game: Scenario) => {
    if (!game?.isLocked || hasSubscription) {
      navigate(GUEST_ROUTES.ROLEPLAY(game?.alias));
    } else {
      if (isSignedIn) {
        onOpenSubscriptionModal();
      } else {
        onOpenAuthModal();
      }
    }
  };

  if (!scenarios || scenarios.length === 0) {
    return null;
  }

  return (
    <ScenariosSectionWrapper>
      <ScenariosSectionTitle>{title}</ScenariosSectionTitle>
      <ScenariosGrid>
        <div className="box-border grid grid-cols-1 gap-8 md:grid-cols-3 lg:grid-cols-4">
          {scenarios?.map(game => {
            const isLikedByMe = myLikedGamesIds.includes(game.id);
            return (
              <GameCard
                key={game.id}
                item={game}
                onItemClick={handleGameCardClick}
                isLikedByMe={isLikedByMe}
                likesToShow={game?.likesNumber}
                shouldShowLikes={!!game?.likesNumber}
                hasSubscription={hasSubscription}
                isVisibleTagSection
              />
            );
          })}
        </div>
      </ScenariosGrid>
    </ScenariosSectionWrapper>
  );
};

export default ScenariosSection;

const ScenariosSectionWrapper = styled.section`
  margin-bottom: 60px;
  
  @media (max-width: 968px) {
    margin-left: 16px;
    margin-right: 16px;
  }
`;

const ScenariosSectionTitle = styled.h2`
  font-family: Raleway;
  font-size: 40px;
  font-weight: 700;
  line-height: 42px;
  color: #FFF;
  margin-bottom: 16px;
`;

const ScenariosGrid = styled.div`
  padding: 16px 0;
`; 